import React, { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogProps } from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { PopupHeader } from "../popup/popup-header.component";
import { GhostButton, PrimaryButton } from "../Buttons";
import { showError } from "utils/notifications";
import style from "../popup/popup.module.css";

dayjs.extend(utc);

interface UpdateSoftwareDialogProps extends Omit<DialogProps, "onClose"> {
  open: boolean;
  onClose: () => void;
  deviceCount: number;
  onConfirm: (updateNow: boolean, updateTime: string) => void;
}

export function UpdateSoftwareDialog({
  open,
  onClose,
  deviceCount,
  onConfirm,
}: UpdateSoftwareDialogProps): JSX.Element {
  const [updateOption, setUpdateOption] = useState<"now" | "later">("now");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().add(1, "hour"));
  const [selectedHour, setSelectedHour] = useState<number>(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour % 12 || 12;
  });
  const [selectedAmPm, setSelectedAmPm] = useState<"AM" | "PM">(() => {
    const nextHour = dayjs().add(1, "hour").hour();
    return nextHour >= 12 ? "PM" : "AM";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => dayjs().add(7, "day").endOf("day"), []);

  // Combine date, hour, and AM/PM into a single DateTime
  const selectedDateTime = useMemo(() => {
    if (!selectedDate) return null;

    let hour24 = selectedHour;
    if (selectedAmPm === "PM" && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (selectedAmPm === "AM" && selectedHour === 12) {
      hour24 = 0;
    }

    return selectedDate.hour(hour24).minute(0).second(0).millisecond(0);
  }, [selectedDate, selectedHour, selectedAmPm]);

  // Initialize date and time when dialog opens
  useEffect(() => {
    if (open) {
      const nextHour = dayjs().add(1, "hour");
      setSelectedDate(nextHour);
      const hour12 = nextHour.hour() % 12 || 12;
      setSelectedHour(hour12);
      setSelectedAmPm(nextHour.hour() >= 12 ? "PM" : "AM");
    }
  }, [open]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdateOption(event.target.value as "now" | "later");
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    setSelectedDate(newValue);
  };

  const handleHourChange = (event: any) => {
    setSelectedHour(Number(event.target.value));
  };

  const handleAmPmChange = (event: any) => {
    setSelectedAmPm(event.target.value as "AM" | "PM");
  };

  const handleConfirm = () => {
    if (updateOption === "later") {
      if (!selectedDateTime) {
        showError("Please select a date and time for scheduling the update.");
        return;
      }

      const now = dayjs();
      if (selectedDateTime.isBefore(now)) {
        showError("Please select a future date and time.");
        return;
      }

      const diffDays = selectedDateTime.diff(now, "day", true);
      if (diffDays > 7) {
        showError("The scheduled time must be within 7 days from now.");
        return;
      }
    }

    setIsSubmitting(true);
    const updateTime =
      updateOption === "later" && selectedDateTime ? (selectedDateTime as any).utc().format("YYYY-MM-DD HH:mm:ss") : "";
    onConfirm(updateOption === "now", updateTime);
  };

  const handleClose = () => {
    setUpdateOption("now");
    const nextHour = dayjs().add(1, "hour");
    setSelectedDate(nextHour.startOf("day"));
    const hour12 = nextHour.hour() % 12 || 12;
    setSelectedHour(hour12);
    setSelectedAmPm(nextHour.hour() >= 12 ? "PM" : "AM");
    onClose();
  };

  return (
    <Dialog className={style.container} fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <PopupHeader title="Update Device Software" />
      <DialogContent>
        <div style={{ marginBottom: "24px" }}>
          <p style={{ margin: 0, fontSize: "16px" }}>
            Update available for {deviceCount} {deviceCount === 1 ? "device" : "devices"}.
          </p>
        </div>

        <FormControl component="fieldset" style={{ width: "100%", marginBottom: "24px" }}>
          <RadioGroup
            aria-label="update-option"
            name="update-option"
            value={updateOption}
            onChange={handleOptionChange}
          >
            <FormControlLabel value="now" control={<Radio />} label="Apply now" />
            <FormControlLabel value="later" control={<Radio />} label="Schedule for later time" />
          </RadioGroup>
        </FormControl>

        {updateOption === "later" && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <DatePicker
                label="Select date"
                value={selectedDate}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="hour-label">Hour</InputLabel>
                <Select
                  labelId="hour-label"
                  id="hour-select"
                  value={selectedHour}
                  label="Hour"
                  onChange={handleHourChange}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((hour) => (
                    <MenuItem key={hour} value={hour}>
                      {hour}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl style={{ minWidth: "120px" }}>
                <InputLabel id="ampm-label">AM/PM</InputLabel>
                <Select
                  labelId="ampm-label"
                  id="ampm-select"
                  value={selectedAmPm}
                  label="AM/PM"
                  onChange={handleAmPmChange}
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        <div style={{ marginBottom: "24px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            <strong>Note:</strong> Please keep the devices ON to apply the update. The update automatically reboots the
            device. The device will go offline for approximately 5-10 mins for MegaFi_2 devices and 20-30 mins for
            MegaFI devices. DO NOT power off the device during this time.
          </p>
        </div>

        <div className={style.alignContentEvenly}>
          <div style={{ marginRight: "16px" }}>
            <GhostButton onClick={handleClose}>Cancel</GhostButton>
          </div>
          <PrimaryButton onClick={handleConfirm}>Update</PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
